/* eslint-disable react/no-multi-comp */
// @flow

import * as React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Box
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

import EnhancedTableHead from './TableHead';
import EnhancedTableToolbar from './TableToolbar';
import DataTableRow from './Row';
import { useLocation, useNavigate } from 'react-router-dom';
import qs from 'qs';

const RootPaper = styled(Paper)({
  width: '100%',
  position: 'relative'
});

const TableWrapper = styled(Box)({
  overflowX: 'auto'
});

const StyledTable = styled(Table)({
  minWidth: 800
});

const LoadingOverlay = styled('div')({
  position: 'absolute',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  width: '100%',
  height: '100%',
  borderRadius: '4px',
  zIndex: 4
});

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

type dataFilters = { [string]: { value: mixed } };

type Props = {
  loading?: boolean,
  data: Array<mixed>,
  order: 'asc' | 'desc',
  orderBy: string,
  defaultFilters: dataFilters,
  defaultHiddenColumnNames: Array<string>,
  defaultRowsPerPage?: number,
  columnData: Array<mixed>,
  selectable?: boolean,
  rowProps?: mixed,
  RowDetail?: mixed,
  setVariables?: (name: string, variables: any) => void,
  totalCount: number,
  pageInfo?: {
    startCursor: string,
    endCursor: string,
    hasNextPage: boolean,
    hasPreviousPage: boolean
  },
  navigate: Function,
  location: {
    pathname: string
  }
};

type State = {
  visibleColumns: Array<string>,
  columnData: Array<mixed>,
  filters: dataFilters,
  order: 'asc' | 'desc',
  orderBy: string,
  selected: Array<string>,
  data: Array<mixed>,
  page: number,
  rowsPerPage: 10 | 25 | 50 | 100,
  expandedRowIds: Array<string>
};

const debounce = (func, delay) => {
  let inDebounce;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func.apply(context, args), delay);
  };
};

class DataTable extends React.Component<Props, State> {
  constructor(props, context) {
    super(props, context);

    const savedColumns = this.props.saveCookieAs
      ? localStorage.getItem(this.props.saveCookieAs)
      : null;
    const visibleColumns = savedColumns
      ? JSON.parse(savedColumns)
      : props.columnData
          .filter(
            (column) => !props.defaultHiddenColumnNames.includes(column.id)
          )
          .map((column) => column.id);

    this.state = {
      visibleColumns: visibleColumns,
      columnData: props.columnData.filter((column) =>
        visibleColumns.includes(column.id)
      ),
      filters: props.defaultFilters || {},
      order: this.props.order || 'asc',
      orderBy: this.props.orderBy || '',
      selected: [],
      data: props.data,
      page: 0,
      rowsPerPage: props.defaultRowsPerPage || DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
      expandedRowIds: []
    };

    this.handleRequestFilter = this.handleRequestFilter.bind(this);
  }

  handleRequestVisibleColumns = (visibleColumns) => {
    if (this.props.saveCookieAs) {
      localStorage.setItem(
        this.props.saveCookieAs,
        JSON.stringify(visibleColumns)
      );
    }
    this.setState(
      {
        visibleColumns: visibleColumns,
        columnData: this.props.columnData.filter((column) =>
          visibleColumns.includes(column.id)
        )
      },
      () => {
        const filter = this.props.columnData.reduce((acc, col) => {
          acc[col.id] = visibleColumns.includes(col.id);
          return acc;
        }, {});
        this.props.setVariables('filter', filter);
      }
    );
  };

  setUrl = (query) => {
    this.props.navigate(
      {
        pathname: this.props.location.pathname,
        hash: `#${qs.stringify(query, {
          encode: false,
          addQueryPrefix: false
        })}`
      },
      { replace: false }
    );
  };

  setQuery = debounce((query) => {
    this.props.setVariables('query', query);
    this.setUrl(query);
  }, 900);

  handleRequestFilter(ev) {
    const { name, value, checked } = ev.target;
    const filters = {
      ...this.state.filters,
      [name]: checked ? checked : value || null
    };
    this.setState({ filters }, () => {
      this.setQuery(filters);
    });
  }

  handleClick = (event, id) => {
    if (this.props.selectable !== true && this.props.RowDetail) {
      const { expandedRowIds } = this.state;
      const expandedIndex = expandedRowIds.indexOf(id);
      let newExpanded = [];

      if (expandedIndex === -1) {
        newExpanded = newExpanded.concat(expandedRowIds, id);
      } else if (expandedIndex === 0) {
        newExpanded = newExpanded.concat(expandedRowIds.slice(1));
      } else if (expandedIndex === expandedRowIds.length - 1) {
        newExpanded = newExpanded.concat(expandedRowIds.slice(0, -1));
      } else if (expandedIndex > 0) {
        newExpanded = newExpanded.concat(
          expandedRowIds.slice(0, expandedIndex),
          expandedRowIds.slice(expandedIndex + 1)
        );
      }

      this.setState({ expandedRowIds: newExpanded });
      return;
    }

    const { selected } = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    this.setState({ selected: newSelected });
  };

  handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const pk = this.props.primaryKey || 'id';
      this.setState({ selected: this.props.data.map((n) => n[pk]) });
      return;
    }
    this.setState({ selected: [] });
  };

  handleRequestSort = (event, property) => {
    if (this.props.setVariables) {
      const orderBy = property.replace(/([A-Z])/g, '_$1').toUpperCase();
      if (this.props.orderBy === orderBy) {
        const order = this.props.order === 'asc' ? 'desc' : 'asc';
        this.props.setVariables('sortDir', order);
      } else {
        this.props.setVariables('sortBy', orderBy);
        this.props.setVariables('sortDir', 'asc');
      }
    }
  };

  handleChangePage = (event, page) => {
    this.props.onPaginationHandler(
      page,
      this.props.pageInfo,
      page - this.state.page
    );
    this.setState({ page });
  };

  handleChangeRowsPerPage = (event) => {
    const rowsPerPage = Number(event.target.value);
    this.setState({ rowsPerPage, page: 0 }, () =>
      this.props.setVariables('rowsPerPage', rowsPerPage)
    );
  };

  isExpanded = (id) =>
    this.props.RowDetail && this.state.expandedRowIds.indexOf(id) !== -1;

  isSelected = (id) =>
    this.props.selectable && this.state.selected.indexOf(id) !== -1;

  i18n = (str, ...args) => {
    return this.props.i18n ? this.props.i18n(str, ...args) : str;
  };

  render() {
    const {
      primaryKey = 'id',
      selectable,
      rowComponent: RowComponent = DataTableRow,
      rowProps = {},
      data,
      order,
      orderBy,
      RowDetail,
      loading
    } = this.props;
    const { selected, columnData, filters, rowsPerPage, page } = this.state;

    const emptyRows = page >= 1 ? data.length - rowsPerPage : 0;

    const columnCount =
      columnData.length + 1 + (RowDetail ? 1 : 0) + (selectable ? 1 : 0);

    return (
      <RootPaper>
        {loading && <LoadingOverlay />}
        <EnhancedTableToolbar
          numSelected={selected.length}
          title={this.props.title}
          i18n={this.i18n}
          columnData={this.props.columnData}
          visibleColumns={this.state.visibleColumns}
          onRequestVisibleColumns={this.handleRequestVisibleColumns}
        />

        <TableWrapper>
          <StyledTable>
            <EnhancedTableHead
              filters={filters}
              onRequestFilter={this.handleRequestFilter}
              columnData={columnData}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={selectable && this.handleSelectAllClick}
              expandable={RowDetail ? true : false}
              onRequestSort={this.handleRequestSort}
              rowCount={data.length}
              i18n={this.i18n}
            />
            <TableBody>
              {data.map(({ node }) => {
                const id = node[primaryKey];
                const classes =
                  typeof rowProps.classes === 'function'
                    ? rowProps.classes(node)
                    : rowProps.classes;

                const row = (
                  <RowComponent
                    key={id}
                    columnData={columnData}
                    id={id}
                    data={node}
                    expandable={RowDetail ? true : false}
                    onRequestExpand={this.handleClick}
                    isExpanded={this.isExpanded(id)}
                    isSelected={this.isSelected(id)}
                    onClick={selectable && this.handleClick}
                    {...rowProps}
                    classes={classes}
                  />
                );

                return RowDetail && this.state.expandedRowIds.includes(id)
                  ? [
                      row,
                      <TableRow key={`${id}-detail`}>
                        <TableCell
                          colSpan={columnCount}
                          style={{ backgroundColor: '#fafafa' }}
                        >
                          <RowDetail id={id} row={node} />
                        </TableCell>
                      </TableRow>
                    ]
                  : row;
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={columnCount} />
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </TableWrapper>

        <TablePagination
          component="div"
          count={this.props.totalCount}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          page={page}
          backIconButtonProps={{
            name: 'prev',
            disabled:
              this.props.pageInfo && !this.props.pageInfo.hasPreviousPage,
            'aria-label': this.props.i18n('Previous Page')
          }}
          nextIconButtonProps={{
            name: 'next',
            disabled: this.props.pageInfo && !this.props.pageInfo.hasNextPage,
            'aria-label': this.props.i18n('Next Page')
          }}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
        />
      </RootPaper>
    );
  }
}

function DataTableWithRouter(props) {
  const navigate = useNavigate();
  const location = useLocation();

  return <DataTable {...props} navigate={navigate} location={location} />;
}

export default DataTableWithRouter;
